import React, { useState, useRef, useEffect } from 'react';
import { Upload, User } from 'lucide-react';
import EditModalWrapper from './EditModalWrapper';

const NATIVE_LANGUAGES = ['English', 'German', 'Malayalam', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Bengali', 'Marathi', 'Gujarati', 'Punjabi', 'Urdu', 'Arabic', 'French', 'Spanish', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Russian', 'Other'];
const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const COUNTRY_CODES = [
    { code: '+91', country: 'India' },
    { code: '+49', country: 'Germany' },
    { code: '+1', country: 'USA' },
    { code: '+44', country: 'UK' },
    { code: '+971', country: 'UAE' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+965', country: 'Kuwait' },
    { code: '+968', country: 'Oman' },
    { code: '+973', country: 'Bahrain' },
];

const EditPersonalDetailsModal = ({ isOpen, onClose, userData, onSave, getPhotoUrl }) => {
    const fileInputRef = useRef(null);

    // Parse phone number into country code and number
    const parsePhone = (phone) => {
        if (!phone) return { countryCode: '+91', number: '' };
        const match = phone.match(/^(\+\d+)\s*(.*)$/);
        if (match) {
            return { countryCode: match[1], number: match[2] };
        }
        return { countryCode: '+91', number: phone };
    };

    // Parse name into first and last name
    const parseName = (name) => {
        if (!name) return { firstName: '', lastName: '' };
        const parts = name.trim().split(' ');
        if (parts.length === 1) return { firstName: parts[0], lastName: '' };
        return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
    };

    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        nativeLanguage: 'English',
        gender: '',
        dateOfBirth: '',
        candidateRefNumber: '',
        aboutMe: '',
        countryCode: '+91',
        phoneNumber: '',
        email: '',
        linkedIn: '',
        facebook: '',
        photo: null,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);

    useEffect(() => {
        if (isOpen) {
            const phoneParts = parsePhone(userData?.phone);
            const nameParts = parseName(userData?.name);
            setForm({
                firstName: userData?.first_name || nameParts.firstName || '',
                lastName: userData?.last_name || nameParts.lastName || '',
                nativeLanguage: userData?.native_language || 'English',
                gender: userData?.gender || '',
                dateOfBirth: (userData?.date_of_birth || userData?.dob || '').split('T')[0],
                candidateRefNumber: userData?.candidate_ref_number || '',
                aboutMe: userData?.about_me || userData?.bio || '',
                countryCode: phoneParts.countryCode,
                phoneNumber: phoneParts.number,
                email: userData?.email || '',
                linkedIn: userData?.linkedin_url || '',
                facebook: userData?.facebook_url || '',
                photo: null,
            });
            setPhotoPreview(null);
        }
    }, [isOpen, userData]);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                return;
            }
            setForm({ ...form, photo: file });
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!form.firstName.trim()) {
            alert('First name is required');
            return;
        }
        if (!form.lastName.trim()) {
            alert('Last name is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('first_name', form.firstName.trim());
            formData.append('last_name', form.lastName.trim());
            formData.append('native_language', form.nativeLanguage);
            formData.append('gender', form.gender);
            formData.append('date_of_birth', form.dateOfBirth);
            formData.append('about_me', form.aboutMe);
            formData.append('phone', `${form.countryCode} ${form.phoneNumber}`);
            formData.append('linkedin_url', form.linkedIn);
            formData.append('facebook_url', form.facebook);

            if (form.photo) {
                formData.append('photo', form.photo);
            }

            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Error saving personal details:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentPhotoUrl = photoPreview || (userData?.photo_url ? getPhotoUrl?.(userData.photo_url) : null);

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Personal Details"
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            size="lg"
        >
            <div className="space-y-6">
                {/* Photo Upload */}
                <div className="flex justify-center mb-6">
                    <div
                        className="relative cursor-pointer group"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {currentPhotoUrl ? (
                            <img
                                src={currentPhotoUrl}
                                alt="Profile"
                                className="w-24 h-24 rounded-full object-cover border-4 border-slate-200 group-hover:border-blue-400 transition-all text-slate-900"
                            />
                        ) : (
                            <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-slate-200 flex items-center justify-center group-hover:border-blue-400 transition-all text-slate-900">
                                <User className="w-12 h-12 text-slate-400" />
                            </div>
                        )}
                        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Upload className="w-6 h-6 text-white" />
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="hidden"
                        />
                    </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            placeholder="Enter first name"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.lastName}
                            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            placeholder="Enter last name"
                            required
                        />
                    </div>
                </div>

                {/* Native Language & Gender */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Native Language <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={form.nativeLanguage}
                            onChange={(e) => setForm({ ...form, nativeLanguage: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                        >
                            {NATIVE_LANGUAGES.map((lang) => (
                                <option key={lang} value={lang}>{lang}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={form.gender}
                            onChange={(e) => setForm({ ...form, gender: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                        >
                            <option value="">Select gender</option>
                            {GENDERS.map((g) => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* DOB & Candidate Ref */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.dateOfBirth}
                            onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Candidate Reference Number
                        </label>
                        <input
                            type="text"
                            value={form.candidateRefNumber}
                            readOnly
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-100 text-slate-500 cursor-not-allowed transition-all"
                            placeholder="Not assigned"
                        />
                    </div>
                </div>

                {/* About Me */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        About Me <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={form.aboutMe}
                        onChange={(e) => setForm({ ...form, aboutMe: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none text-slate-900"
                        placeholder="Write a brief description about yourself..."
                    />
                </div>

                {/* Contact Information Section */}
                <div className="pt-4 border-t border-slate-200">
                    <h3 className="text-base font-semibold text-slate-800 mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Contact Number
                            </label>
                            <div className="flex gap-2">
                                <select
                                    value={form.countryCode}
                                    onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                                    className="w-28 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-sm text-slate-900"
                                >
                                    {COUNTRY_CODES.map((c) => (
                                        <option key={c.code} value={c.code}>{c.code}</option>
                                    ))}
                                </select>
                                <input
                                    type="tel"
                                    value={form.phoneNumber}
                                    onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                                    placeholder="Phone Number"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={form.email}
                                readOnly
                                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </div>

                {/* Social Media Section */}
                <div className="pt-4 border-t border-slate-200">
                    <h3 className="text-base font-semibold text-slate-800 mb-4">Social Media</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                LinkedIn
                            </label>
                            <input
                                type="url"
                                value={form.linkedIn}
                                onChange={(e) => setForm({ ...form, linkedIn: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                                placeholder="Enter url"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                Facebook
                            </label>
                            <input
                                type="url"
                                value={form.facebook}
                                onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                                placeholder="Enter url"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </EditModalWrapper>
    );
};

export default EditPersonalDetailsModal;
